import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import { getApiUrl } from '../config';

export default function HeaderButtons() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [snapshot, setSnapshot] = useState({
    logo: '',
    menu: [],
    mainButton: { text: '', link: '' },
  });
  const [headerButtons, setHeaderButtons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ text: '', link: '' });

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/settings'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setSnapshot({
        logo: data.logo || '',
        menu: data.menu || [],
        mainButton: data.mainButton || { text: '', link: '' },
      });
      setHeaderButtons(Array.isArray(data.headerButtons) ? data.headerButtons : []);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'خطا در دریافت تنظیمات' });
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openAdd = () => {
    setEditIndex(null);
    setForm({ text: '', link: '' });
    setDialogOpen(true);
  };

  const openEdit = (idx) => {
    setEditIndex(idx);
    setForm({
      text: headerButtons[idx]?.text || '',
      link: headerButtons[idx]?.link || '',
    });
    setDialogOpen(true);
  };

  const handleDialogSave = () => {
    if (!form.text.trim() || !form.link.trim()) {
      setMessage({ type: 'error', text: 'متن و لینک دکمه الزامی است' });
      return;
    }
    const next = [...headerButtons];
    if (editIndex !== null) {
      next[editIndex] = { text: form.text.trim(), link: form.link.trim() };
    } else {
      next.push({ text: form.text.trim(), link: form.link.trim() });
    }
    setHeaderButtons(next);
    setDialogOpen(false);
    setMessage({ type: 'success', text: 'در لیست ذخیره شد؛ برای اعمال روی سایت «ذخیره روی سرور» را بزنید' });
  };

  const handleDelete = (idx) => {
    const next = headerButtons.filter((_, i) => i !== idx);
    setHeaderButtons(next);
    setMessage({ type: 'success', text: 'حذف شد؛ برای اعمال روی سایت ذخیره کنید' });
  };

  const handleSubmitServer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('logo', snapshot.logo || '');
      fd.append('menu', JSON.stringify(snapshot.menu || []));
      fd.append('mainButton', JSON.stringify(snapshot.mainButton || { text: '', link: '' }));
      fd.append('headerButtons', JSON.stringify(headerButtons));

      const response = await fetch(getApiUrl('/api/settings'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'دکمه‌های هدر روی سرور ذخیره شد' });
        fetchAll();
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.error || 'خطا در ذخیره' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'خطا در ذخیره تنظیمات' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, direction: 'rtl', textAlign: 'right', fontFamily: 'Vazir' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SmartButtonIcon color="primary" />
        <Typography variant="h5" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
          دکمه‌های هدر
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Vazir' }}>
        دکمهٔ سبز اصلی (CTA) از بخش «تنظیمات سایت» همان‌طور که بود تنظیم می‌شود. اینجا فقط دکمه‌های ثانویهٔ کنار آن را مدیریت کنید.
      </Typography>

      {message.text ? (
        <Alert severity={message.type || 'info'} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Vazir' }}>
              لیست دکمه‌ها
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ fontFamily: 'Vazir' }}>
              افزودن دکمه
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">متن دکمه</TableCell>
                  <TableCell align="right">لینک</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headerButtons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ fontFamily: 'Vazir', color: 'text.secondary' }}>
                      دکمه‌ای ثبت نشده است
                    </TableCell>
                  </TableRow>
                ) : (
                  headerButtons.map((hBtn, idx) => (
                    <TableRow key={idx}>
                      <TableCell align="right">{hBtn.text}</TableCell>
                      <TableCell align="right">{hBtn.link}</TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" onClick={() => openEdit(idx)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleSubmitServer}
            sx={{ fontFamily: 'Vazir', mt: 2 }}
          >
            {loading ? 'در حال ذخیره...' : 'ذخیره روی سرور'}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          {editIndex !== null ? 'ویرایش دکمه هدر' : 'افزودن دکمه هدر'}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="متن دکمه"
            fullWidth
            value={form.text}
            onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
            sx={{ fontFamily: 'Vazir', mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="لینک (مثال /contact)"
            fullWidth
            value={form.link}
            onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))}
            sx={{ fontFamily: 'Vazir' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily: 'Vazir' }}>
            انصراف
          </Button>
          <Button onClick={handleDialogSave} variant="contained" sx={{ fontFamily: 'Vazir' }}>
            {editIndex !== null ? 'ذخیره در لیست' : 'افزودن به لیست'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
